import { Check, ChevronsUpDown } from 'lucide-react'
import { ReactNode, useState } from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Button } from '@/components/lemmala-design-system/button/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/lemmala-design-system/command/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/lemmala-design-system/popover/popover'

export type CommandOption = {
  id: string
  label: string
}

export type CommandSwitcherProps = {
  options: CommandOption[]
  selectedId: string
  selectedLabel?: string
  onSelect: (id: string) => Promise<void> | void
  placeholder?: string
  emptyMessage?: string
  disabled?: boolean
  buttonClassName?: string
  width?: string
  align?: 'start' | 'center' | 'end'
  triggerContent?: ReactNode
}

export function CommandSwitcher({
  options,
  selectedId,
  selectedLabel,
  onSelect,
  placeholder = 'Search...',
  emptyMessage = 'No results found.',
  disabled = false,
  buttonClassName = '',
  width = 'w-56',
  align = 'start',
  triggerContent,
}: CommandSwitcherProps) {
  const [open, setOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleSelect = async (id: string) => {
    if (id === selectedId) {
      setOpen(false)
      return
    }

    setIsLoading(true)
    try {
      await onSelect(id)
      setOpen(false)
    } catch (_error) {
      // Error handling is delegated to the parent through onSelect
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='md'
          className={cn('flex items-center gap-2 whitespace-nowrap font-medium', buttonClassName)}
          disabled={disabled || isLoading}
        >
          {triggerContent || (
            <>
              <span className='truncate'>{selectedLabel || selectedId}</span>
              <ChevronsUpDown className='size-4 shrink-0 opacity-50' />
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={`${width} p-0`} align={align}>
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandEmpty>{emptyMessage}</CommandEmpty>
          <CommandList>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem key={option.id} onSelect={() => handleSelect(option.id)}>
                  <Check
                    className={`mr-2 size-4 ${option.id === selectedId ? 'opacity-100' : 'opacity-0'}`}
                  />
                  <span className='truncate'>{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
